/* Account-session state shared by lib/axios and lib/accountSession, kept in its own module so neither
 * imports the other. Each session carries a number (its "generation"): a request notes the one in
 * force, switching accounts moves it on and cancels the signal, and a reply with an older one is dropped. */

let currentGeneration = 0
let sessionController = new AbortController()

/* The current account session's number. Advances each time an account is torn down. */
export const currentSessionGeneration = (): number => currentGeneration

/* Cancelled on teardown to stop account-scoped work that does not go through the shared axios
 * instance — the raw fetches and streams — when it was started under the previous account. */
export const currentSessionSignal = (): AbortSignal => sessionController.signal

/* Begin a fresh account session: cancel the outgoing signal and advance to the next generation, so
 * anything left over from the previous session counts as stale. Called at the start of teardown. */
export const startNewAccountSession = (): void => {
  sessionController.abort()
  sessionController = new AbortController()
  currentGeneration += 1
}

/* True when work from session `sessionGeneration` belongs to a session that has since ended. */
export const isStaleAccountSession = (sessionGeneration: number): boolean => sessionGeneration !== currentGeneration

/* Marks a response dropped because the account session that started it has ended. Callers such as
 * bootstrap recognize it and discard the result quietly rather than surfacing it as an error. */
export class StaleAccountSessionError extends Error {
  constructor() {
    super('Request discarded: the account session that started it has ended')
    this.name = 'StaleAccountSessionError'
  }
}

/* True when a request was dropped because its account went away. Code that made such a request should
 * notice this and quietly stop, rather than treat it as a failure, since that account is no longer here. */
export const isStaleAccountSessionError = (error: unknown): boolean => error instanceof StaleAccountSessionError
